import { RegionSwitcher } from '../map/RegionSwitcher';
import { Link } from 'react-router';
import { Page } from '../../components/Layout';
import { ErrorState, LoadingState } from '../../components/states';
import { Avatar, Button, Card, LevelBadge, SectionTitle, Stat, XpBar } from '../../components/ui';
import { useEvents, useMyClub, useMyStats, useRegion, useTerritoryStats } from '../../hooks/queries';
import { ClubBadge } from '../clubs/ClubBadge';
import { formatDistance, formatNumber } from '../../lib/format';
import { useTracking } from '../../services/runtime';
import { useAuth } from '../../stores/auth';
import { EventFeed } from './EventFeed';
import { InstallCard } from './InstallCard';
import { HomeStravaCard } from '../imports/HomeStravaCard';
import { EventCard } from '../events/EventCard';
import { MY_COLOR } from '../../lib/colors';
import { NotificationBell } from '../notifications/NotificationBell';
import { useT } from '../../i18n';
import { HomeBanner } from '../campaigns/HomeBanner';

export function HomePage() {
  const user = useAuth((s) => s.user);
  const stats = useMyStats();
  const { region } = useRegion();
  const territory = useTerritoryStats(region?.id);
  const events = useEvents();
  const club = useMyClub();
  const runState = useTracking((s) => s.state);
  const activeRun = runState === 'RUNNING' || runState === 'PAUSED' || runState === 'RESUMING';
  const t = useT();

  const s = stats.data;
  const dist = formatDistance(s?.totalDistanceM ?? 0);

  return (
    <div className="bg-grid-glow min-h-full">
      <Page>
        <header className="flex items-center gap-3">
          <Link to="/profile" className="flex min-w-0 flex-1 items-center gap-3" data-testid="home-profile-link">
            <Avatar name={user?.displayName ?? '?'} color={MY_COLOR} src={user?.avatarUrl} />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">{t('Runner')}</div>
              <div className="truncate font-display text-2xl font-bold" data-testid="home-name">
                {user?.displayName}
              </div>
            </div>
          </Link>
          {s ? <LevelBadge level={s.progress.level} /> : null}
          <NotificationBell />
        </header>

        {stats.isPending ? (
          <LoadingState />
        ) : stats.isError && !s ? (
          <ErrorState error={stats.error} onRetry={() => stats.refetch()} />
        ) : s ? (
          <>
            <Card className="mt-5">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-xl font-bold uppercase">{t('Level {n}', { n: s.progress.level })}</span>
                <span className="text-sm text-ink-300 tabular" data-testid="home-xp">
                  {formatNumber(s.progress.xpIntoLevel)} / {formatNumber(s.progress.xpForLevel)} XP
                </span>
              </div>
              <XpBar into={s.progress.xpIntoLevel} total={s.progress.xpForLevel} className="mt-2" />
              <div className="mt-2 text-xs text-ink-400">{t('{xp} XP total', { xp: formatNumber(s.progress.totalXp) })}</div>
            </Card>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Stat label={t('Territories')} value={s.territoriesOwned} accent="volt" testId="home-territories" />
              <Stat label={t('Rank')} value={s.rank.xp ? `#${s.rank.xp}` : '—'} accent="gold" testId="home-rank" />
              <Stat label={t('Distance')} value={dist.value} unit={dist.unit} />
              <Stat label={t('Streak')} value={s.currentStreak} unit={t(s.currentStreak === 1 ? 'day' : 'days')} accent="sky" />
            </div>
          </>
        ) : null}

        <Link to="/run" className="mt-5 block" data-testid="start-run-cta">
          <Button size="xl" className="w-full">
            {t(activeRun ? 'Back to your run' : 'Start run')}
          </Button>
        </Link>
        <p className="mt-2 text-center text-xs text-ink-500">{t('Run through a sector for 30 m to capture it.')}</p>

        {s && s.validRuns === 0 ? (
          <Link to="/guide" className="mt-4 block" data-testid="new-player-guide">
            <Card className="flex items-center gap-3 border-volt/40">
              <span className="text-3xl">📖</span>
              <span className="flex-1 text-sm">
                <span className="block font-semibold">{t('New here? Read the 2-minute guide')}</span>
                <span className="text-ink-400">{t('Install the game, capture sectors, use your watch.')}</span>
              </span>
              <span className="text-volt">›</span>
            </Card>
          </Link>
        ) : null}
        <HomeBanner regionId={region?.id} />
        <EventCard />
        <HomeStravaCard />
        <InstallCard />

        <SectionTitle action={<Link to="/clubs" className="text-sm font-semibold text-volt">{t('Clubs →')}</Link>}>{t('Club')}</SectionTitle>
        <Link to={club.data?.club ? `/clubs/${club.data.club.id}` : '/clubs'} className="block" data-testid="home-club">
          <Card className="flex items-center gap-3">
            {club.data?.club ? (
              <>
                <ClubBadge club={club.data.club} link={false} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-lg font-bold">{club.data.club.name}</div>
                  <div className="text-xs text-ink-400">
                    {t('{members} members · ⬢ {territories} club territory', { members: club.data.club.stats.members, territories: club.data.club.stats.territories })}
                  </div>
                  {club.data.pendingToReview ? (
                    <div className="text-xs font-semibold text-gold">{t('{n} join request(s) to review', { n: club.data.pendingToReview })}</div>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <span className="hex-clip grid size-12 place-items-center bg-ink-700 text-xl">⬢</span>
                <div className="flex-1">
                  <div className="font-semibold">{t('Join or create a club')}</div>
                  <div className="text-xs text-ink-400">
                    {club.data?.invites.length ? t('{n} invitation(s) waiting', { n: club.data.invites.length }) : t('Conquer the city as a crew')}
                  </div>
                </div>
              </>
            )}
            <span className="text-xl text-ink-500">›</span>
          </Card>
        </Link>

        {region ? (
          <>
            <SectionTitle action={<Link to="/map" className="text-sm font-semibold text-volt">{t('Open map →')}</Link>}>
              <RegionSwitcher />
            </SectionTitle>
            <Card>
              {territory.data ? (
                <div className="grid grid-cols-3 text-center">
                  <MiniStat label={t('Yours')} value={territory.data.mine} color="text-volt" />
                  <MiniStat label={t('Rivals')} value={territory.data.owned - territory.data.mine} color="text-enemy" />
                  <MiniStat label={t('Neutral')} value={territory.data.neutral} color="text-ink-300" />
                </div>
              ) : territory.isError ? (
                <p className="text-sm text-ink-400">{t('Territory stats unavailable offline.')}</p>
              ) : (
                <div className="h-12" />
              )}
            </Card>
          </>
        ) : null}

        <SectionTitle>{t('Battle feed')}</SectionTitle>
        {events.data ? (
          <EventFeed events={events.data} />
        ) : events.isError ? (
          <p className="text-sm text-ink-500">{t('Feed unavailable.')}</p>
        ) : (
          <LoadingState />
        )}
      </Page>
    </div>
  );
}

function MiniStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className={`font-display text-2xl font-bold tabular ${color}`}>{formatNumber(value)}</div>
      <div className="text-[11px] uppercase tracking-widest text-ink-400">{label}</div>
    </div>
  );
}
